import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface DynamicHtmlEvent {
    type: string;
    payload: any;
}

/**
 * Renders dynamic HTML stored in the database and intercepts clicks on embedded
 * entity links, emitting them for the parent to resolve game-specifically.
 *
 * Mark any clickable element in the HTML with two data attributes:
 *   - `data-entity-type`  — the entity category (e.g. "weapon", "special-rule")
 *   - `data-entity-key`   — the entity's lookup key
 *
 * Example:
 *   <a class="entity-link" data-entity-type="weapon" data-entity-key="medium-anti-tank-gun">
 *     Medium Anti-tank Gun
 *   </a>
 *
 * The `entity-link` class applies clickable styling automatically via the
 * `:host ::ng-deep` rules below.
 *
 * The emitted `DynamicHtmlEvent` carries `{ type, payload }` where `payload`
 * is built from the element's `data-entity-*` attributes (excluding `type`).
 */
@Component({
    standalone: true,
    selector: 'abs-dynamic-html',
    template: `<div [innerHTML]="sanitizedHtml" (click)="handleClick($event)"></div>`,
    styles: [
        `
            :host ::ng-deep [data-entity-type],
            :host ::ng-deep .entity-link {
                color: var(--ion-color-primary);
                cursor: pointer;
                text-decoration: underline;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AbsDynamicHtmlComponent implements OnChanges {
    @Input()
    html: string;

    @Output()
    handleEvent = new EventEmitter<DynamicHtmlEvent>();

    sanitizedHtml: SafeHtml = '';

    private sanitizer = inject(DomSanitizer);

    ngOnChanges(changes: SimpleChanges) {
        if (changes['html']) {
            this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(this.html || '');
        }
    }

    handleClick(event: MouseEvent) {
        const target = (event.target as HTMLElement)?.closest('[data-entity-type]') as HTMLElement | null;
        if (!target) {
            return;
        }
        const type = target.getAttribute('data-entity-type');
        if (!type) {
            return;
        }
        event.preventDefault();
        const payload: any = {};
        target
            .getAttributeNames()
            .filter((name) => name.startsWith('data-entity-') && name !== 'data-entity-type')
            .forEach((name) => {
                const camel = name.replace('data-entity-', '');
                payload[camel] = target.getAttribute(name);
            });
        this.handleEvent.emit({ type, payload });
    }
}
